<template>
  <div>
    <h2
      class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
      style="color: var(--text-color)"
    >
      Bulk Change Shows
    </h2>
    <Card class="mb-3">
      <template #content>
        <div class="grid">
          <div class="col-12 md:col-6 flex flex-column gap-1">
            <label for="bulk_mode">Type of bulk change</label>
            <Dropdown
              input-id="bulk_mode"
              v-model="bulk_mode"
              :options="bulk_modes"
              optionLabel="label"
              optionValue="value"
              placeholder="Select a bulk change mode"
              :class="{ 'p-invalid': submitted && !bulk_mode }"
            />
            <small v-if="submitted && !bulk_mode" class="p-error">
              Please select a bulk change mode
            </small>
          </div>

          <div v-if="bulk_mode" class="col-12 flex flex-column gap-1">
            <label for="csv_file">Upload CSV File</label>
            <FileUpload
              ref="csvFileUpload"
              mode="basic"
              accept="text/csv"
              :maxFileSize="1000000"
              @select="onCsvFileSelect"
              customUpload
              :auto="false"
            />
            <small v-if="csvUploadError" class="p-error">
              {{ csvUploadError }}
            </small>
            <download-csv :data="sampleCSV">
              <span
                class="text-blue-500 hover:text-blue-300 underline cursor-pointer"
              >
                Download sample CSV file
              </span>
            </download-csv>
            <div v-if="validData.length > 0" class="mt-2">
              <p class="font-semibold text-green-600">
                ✓ {{ validData.length }} shows will be updated
              </p>
            </div>
          </div>
        </div>
      </template>
    </Card>
    <div class="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        @click="router.go(-1)"
        outlined
        class="p-button-danger"
      />
      <Button
        label="Submit"
        @click="handleSubmit"
        :disabled="!bulk_mode || validData.length === 0 || isLoading"
        :loading="isLoading"
        class="p-button-success"
      />
    </div>
  </div>
</template>

<script setup>
import Card from "primevue/card";
import Dropdown from "primevue/dropdown";
import FileUpload from "primevue/fileupload";
import Button from "primevue/button";
import { ref } from "vue";
import { useRouter } from "vue-router";
import Papa from "papaparse";
import { useToast } from "primevue/usetoast";
import { API_BASE_URL } from "@/consts";
import axios from "axios";

const toast = useToast();
const router = useRouter();

const bulk_mode = ref(null);
const submitted = ref(false);
const csvFileUpload = ref();
const csvUploadError = ref("");
const validData = ref([]);
const isLoading = ref(false);

const bulk_modes = [
  {
    label: "Update RSS Feed URL",
    value: "update_rss_feed_url",
  },
];

const sampleCSV = ref([
  {
    old_rss_feed: "https://example.com/old-feed1.xml",
    new_rss_feed: "https://example.com/new-feed1.xml",
  },
  {
    old_rss_feed: "https://example.com/old-feed2.xml",
    new_rss_feed: "https://example.com/new-feed2.xml",
  },
]);

const onCsvFileSelect = async (event) => {
  csvUploadError.value = "";
  validData.value = [];

  const file = event.files[0];
  if (!file || file.type !== "text/csv") {
    csvUploadError.value = "Please select a valid CSV file";
    return;
  }

  try {
    const results = await new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: resolve,
        error: reject,
      });
    });

    if (!results.data || results.data.length === 0) {
      csvUploadError.value = "CSV file is empty";
      return;
    }

    const requiredFields = ["old_rss_feed", "new_rss_feed"];

    const firstRow = results.data[0];
    const missingFields = requiredFields.filter(
      (field) => !(field in firstRow)
    );

    if (missingFields.length > 0) {
      csvUploadError.value = `Missing required columns: ${missingFields.join(
        ", "
      )}`;
      return;
    }

    const valid = results.data.filter((row) => {
      return row.old_rss_feed?.trim() && row.new_rss_feed?.trim();
    });

    if (!valid.length) {
      csvUploadError.value = "No valid data found in CSV";
      return;
    }

    validData.value = valid.map((row) => ({
      old_rss_feed: row.old_rss_feed.trim(),
      new_rss_feed: row.new_rss_feed.trim(),
    }));

    toast.add({
      severity: "success",
      summary: "Success",
      detail: `${validData.value.length} shows loaded from CSV`,
      life: 3000,
    });
  } catch (error) {
    csvUploadError.value = "Error parsing CSV file";
  }
};

const handleSubmit = async () => {
  submitted.value = true;
  isLoading.value = true;

  try {
    const token = localStorage.getItem("podcastAdminToken");
    const response = await axios.post(
      `${API_BASE_URL}/shows/bulk-change`,
      {
        updateType: bulk_mode.value,
        updateData: validData.value,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const result = response.data;

    if (result.success) {
      toast.add({
        severity: "success",
        summary: "Success",
        detail: `Successfully updated ${validData.value.length} shows`,
        life: 5000,
      });
      setTimeout(() => {
        router.go(-1);
      }, 1500);
    } else {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "An error occurred while updating shows",
        life: 5000,
      });
    }
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail:
        error?.response?.data?.message ||
        "An error occurred while updating shows",
      life: 5000,
    });
  } finally {
    isLoading.value = false;
  }
};
</script>
